import React, { useState } from "react";
import { Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** Language tag of the fenced block the invoice review uses for expandable
 *  reasoning. Raw HTML (`<details>`) isn't rendered in chat, so a tool ships
 *  something collapsible as a fence: the first line is the summary, the rest
 *  is markdown (the findings and their numbered sources). On a viewer that
 *  doesn't know the tag, it is a plain code block, so nothing is lost. */
export const REASONING_LANGUAGE = "reasoning";

export interface ReasoningDetailsProps {
  /** The fence's body: summary line, then markdown. */
  value: string;
}

const REMARK_PLUGINS = [remarkGfm];

export function ReasoningDetails({ value }: ReasoningDetailsProps) {
  const [summary, ...rest] = value.split("\n");
  // Mirrors the native <details> state so the tag says what the next click
  // does; the browser still owns the toggling.
  const [open, setOpen] = useState(false);
  return (
    <Box
      component="details"
      onToggle={(e: React.SyntheticEvent<HTMLDetailsElement>) => setOpen(e.currentTarget.open)}
      sx={{
        my: 0.75,
        pl: 1.5,
        borderLeft: "2px solid",
        borderColor: "divider",
        "& > summary": {
          cursor: "pointer",
          color: "text.secondary",
          fontSize: "0.85rem",
          fontWeight: 600,
          userSelect: "none",
        },
        "& p": { my: 0.5, fontSize: "0.9rem" },
      }}
    >
      <summary data-ph={open ? "reasoning-collapse" : "reasoning-expand"}>{summary.trim()}</summary>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{rest.join("\n")}</ReactMarkdown>
    </Box>
  );
}
